import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trophy, Dumbbell, Trash2 } from "lucide-react";
import { Avatar } from "@/components/Avatar";

export function PRWall() {
  const { t, tl } = useI18n();
  const { user } = useAuth();
  const [lifts, setLifts] = useState([]);
  const [prs, setPrs] = useState([]);
  const [f, setF] = useState({ lift: "", value: "", note: "" });

  const load = () => api.get("/prs").then(({ data }) => setPrs(data));
  useEffect(() => {
    api.get("/prs/lifts").then(({ data }) => { setLifts(data); setF((p) => ({ ...p, lift: data[0] || "" })); });
    load();
  }, []);

  const submit = async () => {
    if (!f.lift || !f.value) return;
    try {
      await api.post("/prs", f);
      toast.success(tl({ es: "¡Récord publicado! 🎉", en: "PR posted! 🎉", fr: "Record publié ! 🎉" }));
      setF({ lift: lifts[0] || "", value: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const kudos = async (pr) => {
    const { data } = await api.post(`/prs/${pr.pr_id}/kudos`);
    setPrs((prev) => prev.map((x) => (x.pr_id === pr.pr_id ? { ...x, kudos_count: data.kudos_count, did_kudos: data.did_kudos } : x)));
  };
  const del = async (id) => { try { await api.delete(`/prs/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "America/Panama" });

  return (
    <AppLayout>
      <h1 className="mb-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Trophy className="h-7 w-7 text-gold" /> {tl({ es: "Muro de récords", en: "PR Wall", fr: "Mur des records" })}
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">{tl({ es: "Comparte tus récords y anima a la comunidad.", en: "Share your PRs and cheer the community.", fr: "Partage tes records et encourage la communauté." })}</p>

      {/* create */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <select data-testid="pr-lift" value={f.lift} onChange={(e) => setF({ ...f, lift: e.target.value })} className="h-11 rounded-lg border border-border bg-background px-3">
            {lifts.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Input data-testid="pr-value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder={tl({ es: "Ej: 100 kg / 3:45", en: "E.g: 225 lbs / 3:45", fr: "Ex: 100 kg / 3:45" })} className="h-11" />
          <Button data-testid="pr-submit" onClick={submit} disabled={!f.value} className="h-11 rounded-full">{tl({ es: "Publicar", en: "Post", fr: "Publier" })}</Button>
        </div>
        <Input data-testid="pr-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={tl({ es: "Nota (opcional)", en: "Note (optional)", fr: "Note (optionnel)" })} className="mt-3 h-11" />
      </div>

      {/* feed */}
      <div className="mt-6 space-y-3">
        {prs.length === 0 && <p className="text-sm text-muted-foreground">{tl({ es: "Aún no hay récords. ¡Sé el primero!", en: "No PRs yet. Be the first!", fr: "Aucun record pour l'instant. Sois le premier !" })}</p>}
        {prs.map((pr) => (
          <div key={pr.pr_id} data-testid={`pr-card-${pr.pr_id}`} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
            <Avatar userId={pr.user_id} name={pr.user_name} size={48} className="!rounded-xl" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{pr.user_name} <span className="text-muted-foreground">· {fmtDate(pr.created_at)}</span></div>
              <div className="font-display text-lg font-bold">{pr.lift} — <span className="text-primary">{pr.value}</span></div>
              {pr.note && <div className="text-xs text-muted-foreground">{pr.note}</div>}
            </div>
            <button data-testid={`kudos-${pr.pr_id}`} onClick={() => kudos(pr)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${pr.did_kudos ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}>
              <Dumbbell className="h-4 w-4" /> {pr.kudos_count}
            </button>
            {(pr.user_id === user?.user_id || user?.role === "admin") && (
              <button data-testid={`del-pr-${pr.pr_id}`} onClick={() => del(pr.pr_id)} className="shrink-0 text-destructive hover:opacity-80"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
